import { ThemeProvider, useTheme } from "@mui/material";
import { BarChart, LineChart } from "echarts/charts";
import { GridComponent, LegendComponent, TooltipComponent } from "echarts/components";
import * as echarts from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import ReactEChartsCore from "echarts-for-react/lib/core";
import { flushSync } from "react-dom";
import { createRoot } from "react-dom/client";

import { Box } from "@/Components/v2/atoms/Box";
import { generateColorPalette } from "@/context/ThemeProvider/theme";
import { ABBREVIATED_OPTION, NO_SYMBOL, useConfig } from "@/hooks/useConfig";
import { formatAxisLabel } from "@/shared/utils/formatAxisLabel";
import { formatTooltipDate } from "@/shared/utils/formatTooltipDate";
import { colors } from "@/Utils";
import { getMappedName } from "@/Utils/mapping-utils";

import {
  ChannelData,
  ChartToggle,
  RevenueDataPoint,
  ROIDataPoint,
  TotalSpendDataPoint,
} from "@/pages/flighting/types/common";
import TooltipResolver from "@/pages/flighting/components/Chart/Tooltip/TooltipResolver";

echarts.use([
  BarChart,
  LineChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  CanvasRenderer,
]);

const SHORT_DURATION_DATA_POINTS_LENGTH = 15;
const LONG_DURATION_DATA_POINTS_LENGTH = 52;

interface ROIChartProps {
  roiData: ROIDataPoint[];
  revenueData: RevenueDataPoint[];
  totalSpendData: TotalSpendDataPoint[];
  toggles: ChartToggle[];
}

const calculateGridBottom = (legendData: string[]) => {
  // Will need to find a better way to calculate this
  const legendDataLength = legendData.reduce((sum, name) => sum + name.length, 0);
  // Estimate 80 characters per row
  const estimateLegendRowCount = Math.floor(legendDataLength / 80);
  // Add 5% for each row
  return `${10 + estimateLegendRowCount * 5}%`;
};

const getLegendData = (
  channelBySpend: string[],
  toggles: ChartToggle[],
  hasROIData: boolean,
  roiLabel: string,
  revenueLabel: string,
  allTogglesSelected: boolean
) => {
  const toggleLabels = toggles.map((item) => {
    if (item === ChartToggle.REVENUE) {
      return revenueLabel;
    }
    if (item === ChartToggle.ROI && hasROIData) {
      return roiLabel;
    }
    return item;
  });

  // When all toggles are selected, individual channels are replaced by aggregated spend line
  // so we should only include toggle labels in the legend
  if (allTogglesSelected) {
    return toggleLabels;
  }

  return [...channelBySpend, ...toggleLabels];
};

const getLeftAxisLabel = (
  shouldShowSpend: boolean,
  shouldShowRevenue: boolean,
  shouldShowROIData: boolean,
  revenueLabel: string
): string => {
  // When all 3 variables are selected, Spend and Revenue share the left axis
  if (shouldShowSpend && shouldShowRevenue && shouldShowROIData) {
    return `Spend and ${revenueLabel}`;
  }
  // When Revenue is enabled without Spend and without ROI, show "Revenue"
  if (shouldShowRevenue && !shouldShowSpend) {
    return revenueLabel;
  }
  // Default to "Spend" for all other cases (including Revenue+ROI without Spend, and Spend+Revenue without ROI)
  return "Spend";
};

const getRightAxisLabel = (
  shouldShowSpend: boolean,
  shouldShowRevenue: boolean,
  shouldShowROIData: boolean,
  revenueLabel: string
): string | null => {
  // Right axis is only shown when ROI is enabled
  if (shouldShowROIData) {
    return "ROI %";
  }
  // When Spend and Revenue are enabled (without ROI), Revenue should be on right
  if (shouldShowSpend && shouldShowRevenue && !shouldShowROIData) {
    return revenueLabel;
  }
  return null;
};

const getLeftAxisMaxValue = (
  revenueAxisIndex: number,
  shouldShowRevenue: boolean,
  shouldShowSpend: boolean,
  roundedMaxSpend: number,
  roundedMaxRevenue: number
): number => {
  // When Revenue is on right axis, left axis only needs to consider Spend
  // When Revenue is on left axis (all 3 selected), both Spend and Revenue share left axis
  // When Revenue is on left axis without Spend, only consider Revenue
  if (revenueAxisIndex === 0 && shouldShowRevenue) {
    // Revenue is on the left axis
    if (shouldShowSpend) {
      // Both Spend and Revenue share the left axis
      return Math.max(roundedMaxSpend, roundedMaxRevenue) || 1;
    } else {
      // Only Revenue is on the left axis (Spend is not selected)
      return roundedMaxRevenue || 1;
    }
  } else {
    // Revenue is on the right axis or not shown, left axis only shows Spend
    return roundedMaxSpend || 1;
  }
};

const ROIChart: React.FC<ROIChartProps> = ({ roiData, revenueData, totalSpendData, toggles }) => {
  const theme = useTheme();
  const { config } = useConfig();
  const { revenueLabel, showReturnInsteadOfRevenue } = config;
  const revenueFormatterOptions = showReturnInsteadOfRevenue
    ? { ...NO_SYMBOL, ...ABBREVIATED_OPTION }
    : ABBREVIATED_OPTION;

  if (!roiData || roiData.length === 0) {
    return <div>No data available</div>;
  }

  const channelNames = Object.keys(roiData[0] || {}).filter(
    (key) => key !== "date" && key !== "roi" && key !== "hideROI"
  );

  const channelColors = generateColorPalette(
    channelNames.length,
    theme.palette.ROILineChart.barChartColors
  );

  // Calculate min and max ROI values
  const roiValues = roiData
    .filter((item) => !item.hideROI)
    .map((item) => item.roi)
    .filter((roi): roi is number => roi != null && roi !== 0);
  const minROI = Math.min(...roiValues);
  const maxROI = Math.max(...roiValues);

  // Round minROI down and maxROI up to nearest 50
  const roundedMinROI = Math.floor(minROI / 50) * 50;
  const roundedMaxROI = Math.ceil(maxROI / 50) * 50;

  const spendPerChannel: {
    [key: string]: number;
  } = channelNames.reduce(
    (acc, channel) => ({
      ...acc,
      [channel]: roiData.reduce((a, item) => a + ((item[channel] as number) ?? 0), 0),
    }),
    {}
  );

  const channelBySpend = Object.keys(spendPerChannel).sort(
    (a, b) => spendPerChannel[b]! - spendPerChannel[a]!
  );
  // Calculate total spend and ROI values
  const spendPerWeek = roiData.map((item) =>
    channelBySpend.reduce((sum, channel) => sum + ((item[channel] as number) ?? 0), 0)
  );
  const maxTotalSpend = spendPerWeek.length > 0 ? Math.max(...spendPerWeek) : 0;
  const roundedMaxSpend = maxTotalSpend > 0 ? getNiceMaxValue(maxTotalSpend) : 0;

  const hasROIData = roiData.some((item) => !item.hideROI && item.roi != null && item.roi !== 0);

  const shouldShowRevenue = toggles.includes(ChartToggle.REVENUE);
  const shouldShowSpend = toggles.includes(ChartToggle.SPEND);
  const shouldShowROIData = toggles.includes(ChartToggle.ROI) && hasROIData;

  // Filter out invalid revenue values and handle empty arrays
  // Only calculate revenue max if revenue is being displayed
  const validRevenueValues = shouldShowRevenue
    ? revenueData
        .map((item) => Number(item.totalRevenue))
        .filter((value) => !isNaN(value) && isFinite(value) && value > 0)
    : [];
  const maxRevenue = validRevenueValues.length > 0 ? Math.max(...validRevenueValues) : 0;
  const roundedMaxRevenue = maxRevenue > 0 ? getNiceMaxValue(maxRevenue) : 0;

  const allTogglesSelected = shouldShowRevenue && shouldShowSpend && shouldShowROIData;

  const roiLabel = "Rolling ROI";
  const legendData = getLegendData(
    channelBySpend,
    toggles,
    hasROIData,
    roiLabel,
    revenueLabel,
    allTogglesSelected
  );

  // Determine axis labels based on toggle states
  const leftAxisLabel = getLeftAxisLabel(
    shouldShowSpend,
    shouldShowRevenue,
    shouldShowROIData,
    revenueLabel
  );
  const rightAxisLabel = getRightAxisLabel(
    shouldShowSpend,
    shouldShowRevenue,
    shouldShowROIData,
    revenueLabel
  );

  // Determine which axis Revenue should use
  // When Spend and Revenue are enabled but ROI is not, Revenue goes on right axis
  const revenueAxisIndex = shouldShowSpend && shouldShowRevenue && !shouldShowROIData ? 1 : 0;

  // Calculate chartMaxValue for left axis
  const chartMaxValue = getLeftAxisMaxValue(
    revenueAxisIndex,
    shouldShowRevenue,
    shouldShowSpend,
    roundedMaxSpend,
    roundedMaxRevenue
  );

  const channelSpendSeriesData = channelBySpend.map((channel, index) => ({
    name: channel,
    type: "bar",
    stack: "total",
    yAxisIndex: 0,
    data: roiData.map((item) => item[channel] as number),
    itemStyle: { color: channelColors[index] },
    barMaxWidth: 50,
  }));

  const totalSpendLineSeriesData = {
    name: ChartToggle.SPEND,
    type: "line",
    yAxisIndex: 0,
    data: totalSpendData.map((item) => item.totalSpend),
    itemStyle: { color: theme.palette.SpendLineChart.lineColor },
    lineStyle: { color: theme.palette.SpendLineChart.lineColor },
    connectNulls: true,
  };

  const spendSeriesData = allTogglesSelected ? [totalSpendLineSeriesData] : channelSpendSeriesData;

  const option = {
    tooltip: {
      trigger: "axis",
      backgroundColor: theme.palette.echartTooltip.background,
      textStyle: {
        color: theme.palette.echartTooltip.textColor,
      },
      formatter: function (params: any[]) {
        const date = formatTooltipDate(params[0].name);
        const roiParam = params.find(
          (p: { seriesName: string; value: number }) => p.seriesName === roiLabel
        );
        const rollingROI = roiParam && roiParam.value !== "-" ? roiParam.value : null;
        const channels = params
          .filter((p) => p.seriesName !== roiLabel && p.seriesName !== revenueLabel)
          .map((p) => ({
            name: p.seriesName,
            value: p.value ?? 0,
            color: p.color,
          }))
          .reverse() as ChannelData[];
        const totalSpend = channels.reduce((sum, channel) => sum + channel.value, 0);

        const revenueParam = params.find(
          (p: { seriesName: string; value: number }) => p.seriesName === revenueLabel
        );
        const totalRevenue = revenueParam && revenueParam.value !== "-" ? revenueParam.value : 0;

        const tooltipRoot = document.createElement("div");
        const root = createRoot(tooltipRoot);
        flushSync(() => {
          root.render(
            <ThemeProvider theme={theme}>
              <Box
                bgcolor={theme.palette.mode === "light" ? colors.white : colors.cardBg}
                color={theme.palette.mode === "light" ? colors.cardBg : colors.white}
              >
                <TooltipResolver
                  date={date}
                  rollingROI={rollingROI}
                  channels={channels}
                  totalSpend={totalSpend}
                  totalRevenue={totalRevenue}
                  toggles={toggles}
                  hasROIData={hasROIData}
                  config={config}
                />
              </Box>
            </ThemeProvider>
          );
        });
        return tooltipRoot.innerHTML;
      },
    },
    legend: {
      data: legendData,
      textStyle: {
        color: theme.palette.text.primary,
      },
      bottom: 0,
      formatter: function (name: string) {
        if (name === roiLabel) return name;
        return getMappedName(name) || name;
      },
    },
    grid: {
      left: "3%",
      right: "4%",
      bottom: calculateGridBottom(legendData),
      containLabel: true,
    },
    xAxis: [
      {
        type: "category",
        data: roiData.map((item) => item.date),
        boundaryGap: true,
        axisPointer: {
          type: "shadow",
        },
        axisLabel: {
          formatter: (value: string, index: number) =>
            formatAxisLabel(value, roiData.length, index),
          interval: (index: number, value: string) => {
            // For weekly and 3-month views (up to 15 bars)
            if (roiData.length <= SHORT_DURATION_DATA_POINTS_LENGTH) {
              return true; // Show all labels
            }

            // Rest of the logic for monthly and all-time views
            if (roiData.length > LONG_DURATION_DATA_POINTS_LENGTH) {
              const currentDate = new Date(value);
              const isFirstPoint = index === 0;
              const isLastPoint = index === roiData.length - 1;
              const isFirstMonthOfQuarter = currentDate.getMonth() % 3 === 0;
              const isFirstDateOfMonth =
                roiData.findIndex(
                  (item) =>
                    new Date(item.date).getMonth() === currentDate.getMonth() &&
                    new Date(item.date).getFullYear() === currentDate.getFullYear()
                ) === index;

              return isFirstPoint || isLastPoint || (isFirstMonthOfQuarter && isFirstDateOfMonth);
            }

            // Monthly view (16 to 52 bars)
            const currentDate = new Date(value);
            const currentMonthYear = currentDate.toLocaleString("en-US", {
              month: "short",
              year: "numeric",
            });

            const monthIndices = roiData
              .map((item, idx) => ({
                date: new Date(item.date),
                index: idx,
              }))
              .filter(
                (item) =>
                  item.date.toLocaleString("en-US", {
                    month: "short",
                    year: "numeric",
                  }) === currentMonthYear
              )
              .map((item) => item.index);

            const middleIndex = monthIndices[Math.floor(monthIndices.length / 2)];
            return index === middleIndex;
          },
          align: "center",
          textStyle: {
            color: theme.palette.text.primary,
            fontWeight: 400,
            fontSize: "0.8rem",
            fontFamily: "Montserrat",
          },
        },
      },
    ],
    yAxis: [
      {
        type: "value",
        name: leftAxisLabel,
        nameLocation: "end",
        nameGap: 45,
        nameTextStyle: {
          color: theme.palette.text.primary,
          fontWeight: 600,
          fontSize: "0.8rem",
          fontFamily: "Montserrat",
          align: allTogglesSelected ? "center" : "right",
          verticalAlign: "top",
          padding: [0, 0, 10, 0],
          overflow: "break",
        },
        min: 0,
        max: chartMaxValue,
        interval: chartMaxValue / 5, // Set interval to ensure even distribution
        axisLabel: {
          formatter: shouldShowSpend
            ? (value: number) => config.currency.formatCurrency(value, ABBREVIATED_OPTION)
            : (value: number) => config.currency.formatCurrency(value, revenueFormatterOptions),
          textStyle: {
            color: theme.palette.text.primary,
            fontWeight: 400,
            fontSize: "0.8rem",
            fontFamily: "Montserrat",
          },
        },
        splitLine: {
          show: false,
        },
        position: "left",
      },
      ...(rightAxisLabel
        ? [
            {
              type: "value",
              name: rightAxisLabel,
              nameLocation: "end",
              nameGap: 45,
              nameTextStyle: {
                color: theme.palette.text.primary,
                fontWeight: 600,
                fontSize: "0.8rem",
                fontFamily: "Montserrat",
                align: "left",
                verticalAlign: "top",
                padding: [0, 0, 10, 10],
              },
              min: shouldShowROIData ? roundedMinROI : 0,
              max: shouldShowROIData ? roundedMaxROI : roundedMaxRevenue || 1,
              interval: shouldShowROIData
                ? (roundedMaxROI - roundedMinROI) / 5
                : (roundedMaxRevenue || 1) / 5, // Set interval to ensure even distribution
              axisLabel: {
                formatter: shouldShowROIData
                  ? "{value}%"
                  : (value: number) =>
                      config.currency.formatCurrency(value, revenueFormatterOptions),
                textStyle: {
                  color: theme.palette.text.primary,
                  fontWeight: 400,
                  fontSize: "0.8rem",
                  fontFamily: "Montserrat",
                },
              },
              splitLine: {
                show: false,
              },
              position: "right",
            },
          ]
        : []),
    ],
    series: [
      ...(shouldShowSpend ? spendSeriesData : []),
      ...(shouldShowROIData
        ? [
            {
              name: roiLabel,
              type: "line",
              yAxisIndex: 1,
              data: roiData.map((item) => (!item.hideROI && item.roi != null ? item.roi : "-")),
              itemStyle: { color: theme.palette.ROILineChart.lineColor },
              lineStyle: { color: theme.palette.ROILineChart.lineColor },
              connectNulls: true,
            },
          ]
        : []),
      ...(shouldShowRevenue
        ? [
            {
              name: revenueLabel,
              type: "line",
              yAxisIndex: revenueAxisIndex,
              data: revenueData.map((item) => item.totalRevenue),
              itemStyle: { color: theme.palette.RevenueLineChart.lineColor },
              lineStyle: { color: theme.palette.RevenueLineChart.lineColor },
              connectNulls: true,
            },
          ]
        : []),
    ],
  };

  return (
    <ReactEChartsCore
      echarts={echarts}
      option={option}
      notMerge={true}
      lazyUpdate={true}
      style={{ height: "30rem" }}
    />
  );
};

export default ROIChart;

const getNiceMaxValue = (maxValue: number) => {
  // Handle edge cases: NaN, Infinity, negative, or zero values
  if (!isFinite(maxValue) || isNaN(maxValue) || maxValue <= 0) {
    return 0;
  }
  const exponent = Math.floor(Math.log10(maxValue));
  const factor = Math.ceil(maxValue / Math.pow(10, exponent));
  return factor * Math.pow(10, exponent);
};
